import { marker } from "@jsverse/transloco-keys-manager/marker";
import { some } from "lodash-es";
import { RoleType } from "src/app/backend/models/role/role";
import { absoluteUrl } from "src/app/support/navigation";
import { NavigationBarItem } from "../bootstrap-components/navigation-bar/navigation-bar-item";
import { sntTranslate } from "src/app/support/translate";
import { routes_rocket } from "src/app/routing/routes";
import { UserService } from "src/app/services/user.service";
import { appState } from "src/app/core/state";
import { BootstrapService } from "src/app/modules/bootstrap/bootstrap.service";
import { COLORS_FOR_MODULES } from "src/app/support/colors";

export function getNavigationBarMenu(
  currentRoles: RoleType[],
  {
    userService,
    bootstrapService
  }: { userService: UserService; bootstrapService: BootstrapService }
): NavigationBarItem[] {
  const requestsItems: NavigationBarItem[] = filterMenuItemByRole(
    currentRoles,
    [
      {
        icon: "fa-light fa-folder-open",
        title: sntTranslate(marker("Ordini di lavoro")),
        url: absoluteUrl("/requests"),
        disabled: userService.unable("read", "request"),
        params: {
          tenant: appState.tenantId!
        }
      }
    ]
  );

  const studiesItems: NavigationBarItem[] = filterMenuItemByRole(currentRoles, [
    {
      icon: "fa-light fa-clipboard-medical",
      title: sntTranslate(marker("Tutti")),
      url: routes_rocket.studies.master(),
      roles: { unauthorized: ["reporter"] },
      disabled: userService.unable("read", "study")
    },
    {
      icon: "fa-light fa-tape",
      title: sntTranslate(marker("Archiviati")),
      url: routes_rocket.studies.archived(),
      disabled:
        !bootstrapService.getCurrentConfiguration()?.archive?.enabled ||
        userService.unable("read", "study")
    },
    {
      icon: "fa-light fa-clipboard-list",
      title: sntTranslate(marker("Da refertare")),
      url: routes_rocket.reporting.master(),
      disabled: userService.unable("read", "study")
    },
    {
      icon: "fa-light fa-clipboard-check",
      title: sntTranslate(marker("Refertati")),
      url: routes_rocket.reporting.reported(),
      disabled: userService.unable("read", "study")
    }
  ]);

  const analyticsMenuItems: NavigationBarItem[] = [
    {
      icon: "fa-clipboard-medical",
      title: sntTranslate(marker("Studi")),
      url: routes_rocket.analytics.studies.master(),
      disabled: userService.unable("read", "analytics")
    }
  ];

  const registryItems: NavigationBarItem[] = filterMenuItemByRole(
    currentRoles,
    [
      {
        icon: "fa-light fa-users",
        title: sntTranslate(marker("Contatti")),
        url: absoluteUrl("/customers"),
        disabled: userService.unable("read", "customer"),
        params: {
          tenant: appState.tenantId!
        }
      },
      {
        icon: "fa-light fa-paw",
        title: sntTranslate(marker("Pazienti")),
        url: absoluteUrl("/patients"),
        disabled: userService.unable("read", "patient"),
        params: {
          tenant: appState.tenantId!
        }
      },
      {
        icon: "fa-light fa-user-doctor",
        title: sntTranslate(marker("Medici esterni")),
        url: routes_rocket.configuration.physicians.master(),
        disabled: userService.unable("read", "physician"),
        params: {
          tenant: appState.tenantId!
        }
      }
    ]
  );

  const usersItems: NavigationBarItem[] = filterMenuItemByRole(currentRoles, [
    {
      icon: "fa-light fa-user-friends",
      title: sntTranslate(marker("Utenti")),
      url: absoluteUrl("/configuration/manage-users/members"),
      disabled: userService.unable("read", "member")
    },
    {
      icon: "fa-light fa-users",
      title: sntTranslate(marker("Gruppi")),
      url: absoluteUrl("/configuration/manage-users/groups"),
      disabled: userService.unable("read", "group")
    },
    {
      icon: "fa-light fa-key",
      title: sntTranslate(marker("Ruoli")),
      url: absoluteUrl("/configuration/manage-users/roles"),
      disabled: userService.unable("read", "role")
    }
  ]);

  const configurationsItems: NavigationBarItem[] = filterMenuItemByRole(
    currentRoles,
    [
      {
        icon: "fa-light fa-box",
        title: sntTranslate(marker("Box")),
        url: absoluteUrl("/configuration/general"),
        disabled: userService.unable("read", "setting"),
        params: {
          tenant: appState.tenantId
        }
      },
      {
        icon: "fa-light fa-archive",
        title: sntTranslate(marker("Archivi")),
        url: absoluteUrl("/configuration/archives"),
        disabled: userService.unable("read", "study"),
        params: {
          tenant: appState.tenantId
        }
      },
      {
        icon: "fa-light fa-display-medical",
        title: sntTranslate(marker("Dispositivo")),
        url: absoluteUrl("/configuration/devices"),
        disabled: userService.unable("read", "device"),
        params: {
          tenant: appState.tenantId
        }
      },
      {
        icon: "fa-light fa-envelope-square",
        title: sntTranslate(marker("Modello refertazione")),
        url: routes_rocket.configuration.template_report.master(),
        disabled: userService.unable("read", "template"),
        params: {
          tenant: appState.tenantId
        }
      },
      {
        icon: "fa-light fa-heading",
        title: sntTranslate(marker("Carta intestata")),
        url: routes_rocket.configuration.letterhead.master(),
        disabled: userService.unable("read", "letterhead"),
        params: {
          tenant: appState.tenantId
        }
      },
      {
        icon: "fa-tags",
        title: sntTranslate(marker("Tag")),
        url: routes_rocket.configuration.tag.master(),
        disabled: userService.unable("read", "tag"),
        params: {
          tenant: appState.tenantId
        }
      }
    ]
  );

  return filterMenuItemByRole(currentRoles, [
    {
      title: sntTranslate(marker("Ordini di lavoro")),
      icon: "fa-light fa-folder-open",
      url: absoluteUrl("/requests"),
      roles: { unauthorized: ["reporter"] },
      items: requestsItems,
      disabled: userService.unable("read", "request"),
      params: {
        tenant: appState.tenantId!
      },
      color: COLORS_FOR_MODULES.ROCKET_WORK_ORDERS
    },
    {
      icon: "fa-light fa-clipboard-medical",
      title: sntTranslate(marker("Studi")),
      url: absoluteUrl("/studies"),
      items: studiesItems,
      disabled: userService.unable("read", "study"),
      params: {
        tenant: appState.tenantId!
      },
      color: COLORS_FOR_MODULES.ROCKET_STUDIES
    },
    {
      title: sntTranslate(marker("Anagrafica")),
      icon: "fa-light fa-address-book",
      url: absoluteUrl("/customers"),
      roles: { unauthorized: ["reporter"] },
      items: registryItems,
      disabled:
        userService.unable("read", "customer") &&
        userService.unable("read", "patient"),
      params: {
        tenant: appState.tenantId!
      },
      color: COLORS_FOR_MODULES.REGISTRY
    },
    {
      icon: "fa-light fa-users-cog",
      title: sntTranslate(marker("Gestione utenti")),
      roles: { unauthorized: ["reporter"] },
      items: usersItems,
      disabled:
        userService.unable("read", "member") &&
        userService.unable("read", "group") &&
        userService.unable("read", "role"),
      color: COLORS_FOR_MODULES.MANAGE_USERS
    },
    {
      icon: "fa-file-export",
      title: sntTranslate(marker("Esportazioni")),
      url: routes_rocket.configuration.exports.master(),
      disabled: userService.unable("read", "export"),
      params: {
        tenant: appState.tenantId!
      },
      color: COLORS_FOR_MODULES.ROCKET_EXPORTS
    },
    {
      icon: "fa-clock-rotate-left",
      title: sntTranslate(marker("Backup")),
      url: routes_rocket.configuration.backups.master(),
      disabled: userService.unable("read", "setting"),
      params: {
        tenant: appState.tenantId!
      },
      color: COLORS_FOR_MODULES.ROCKET_BACKUP
    },
    {
      icon: "fa-light fa-siren-on",
      title: sntTranslate(marker("Allarmi")),
      url: routes_rocket.alarms.master(),
      disabled: false,
      params: {
        tenant: appState.tenantId!
      },
      color: COLORS_FOR_MODULES.ALARMS
    },
    {
      icon: "fa-chart-mixed",
      title: sntTranslate(marker("Statistiche")),
      url: routes_rocket.analytics.studies.master(),
      items: analyticsMenuItems,
      disabled: some(
        analyticsMenuItems.filter((x) => !x.notImplemented),
        (x) => x.disabled
      ),
      color: COLORS_FOR_MODULES.STATISTICS
    },
    {
      title: sntTranslate(marker("Configurazioni")),
      icon: "fa-light fa-cog",
      url: absoluteUrl("/configuration"),
      roles: { unauthorized: ["physician", "reporter"] },
      items: configurationsItems,
      disabled:
        userService.unable("read", "setting") &&
        userService.unable("read", "study") &&
        userService.unable("read", "device") &&
        userService.unable("read", "template") &&
        userService.unable("read", "letterhead") &&
        userService.unable("read", "physician"),
      color: COLORS_FOR_MODULES.ROCKET_CONFIGURATIONS
    },
    {
      icon: "fa-circle-question",
      title: sntTranslate(marker("Centro assistenza")),
      url: routes_rocket.help_center.master(),
      disabled: false,
      color: COLORS_FOR_MODULES.DEFAULT
    }
  ]);
}

function filterMenuItemByRole(
  currentRoles: RoleType[],
  items: NavigationBarItem[]
): NavigationBarItem[] {
  return items.filter((x) => isMenuItemAuthorized(currentRoles, x));
}

function isMenuItemAuthorized(
  currentRoles: RoleType[],
  menu: NavigationBarItem
): boolean {
  if (menu.roles) {
    if (menu.roles.unauthorized) {
      return some(
        currentRoles,
        (role) => !menu.roles!.unauthorized!.includes(role)
      );
    }
    if (menu.roles.authorized) {
      return some(currentRoles, (role) =>
        menu.roles!.authorized!.includes(role)
      );
    }
  }

  return true;
}
